<template>
  <o-modal :active="isActive" class="modal-sm" :canCancel="false">
    <o-loading :full-page="true" :active="isLoading" :can-cancel="false"></o-loading>
    <div class="modal-card">
      <header class="modal-card-head is-borderless">
        <h4 class="modal-card-title">
          {{ mode === 'create' ? 'Create New User' : `Edit User ${user.fullName}` }}
          <span class="line-height required-label is-size-5 is-italic is-pulled-right mr-0 has-text-weight-normal">
            * required
          </span>
        </h4>
      </header>
      <div class="modal-card-body is-visible px-0 p-0">
        <div class="column">
          <div class="columns">
            <div class="column pt-0">
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3"> First Name* </label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input class="input" v-model.trim="currentUser.firstName"
                      placeholder="Enter name (3 or more characters)" />
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3"> Last Name* </label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input class="input" v-model.trim="currentUser.lastName"
                      placeholder="Enter last name (3 or more characters)" />
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3"> Mobile </label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input class="input" v-model.trim="currentUser.mobilePhone" placeholder="Enter Mobile Phone" />
                  </div>
                </div>
              </div>
              <div class="columns">
                <div class="column is-narrow">
                  <div class="field">
                    <div class="field-label">
                      <label class="label"> Licensed User </label>
                    </div>
                    <div class="field-body">
                      <div class="control">
                        <o-checkbox v-model="currentUser.hasReservedLicense"  class="mr-0 pt-0 pl-0 ml-0" @update:modelValue="updateLicense" :disabled="disableField('license')" />
                      </div>
                    </div>
                  </div>

                </div>
                <div class="column">
                  <div class="field">
                    <div class="field-label">
                      <label class="label is-size-3"> Permission Group </label>
                    </div>
                    <div class="field-body">
                      <mf-multi-select v-model="currentUser.permissionGroup" :options="permissionGroupList"
                        track-by="_id" label="name" :multiple="false" :allow-empty="false" :closeOnSelect="true"
                        placeholder="Permission Group" :disabled="!currentUser.hasReservedLicense || disableField('permissionGroup')">
                      </mf-multi-select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="column pt-0">
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3"> Email* </label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input class="input" v-model.trim="currentUser.email" placeholder="Enter Email" :disabled="disableField('email')"/>
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3"> Role </label>
                </div>
                <div class="field-body">
                  <mf-multi-select v-model="currentUser.role" :options="roles" track-by="name" label="name"
                    :multiple="false" :allow-empty="false" :closeOnSelect="true" placeholder="Role">
                  </mf-multi-select>
                </div>
              </div>

              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3"> Add To New Projects </label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <o-checkbox v-model="currentUser.autoAdd" class="mr-0  pt-0 pl-0 ml-0" :disabled="disableField('addToProjects')"/>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
      <footer class="modal-card-foot is-justify-content-flex-end is-align-items-center">
        <div class="column is-narrow">
          <div>
            <button class="button is-outlined" @click="$emit('close', {refresh: false})">
              Cancel
            </button>
            <button class="button has-background-black-bis" @click="saveUser()"
              :disabled="disableUpdate">
              {{ mode === 'create' ? 'Create New User' : 'Update User' }}
            </button>
          </div>
        </div>
      </footer>
    </div>
  </o-modal>
</template>

<script>
import { reactive, toRefs, computed, onMounted, onBeforeMount } from "vue";
import _ from "lodash";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import Validation from '@/components/utils/Validations';
import Users from '@/models/Users';
import { DialogProgrammatic } from '@/components/Dialog';


export default {
  name: "UserFormModal",
  emits: ["close", "create"],
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object,
      default: () => { }
    },
    mode: {
      type: String,
      default: 'create',
    }
  },

  setup(props, { emit }) {
    const store = useStore();
    const toast = useToast();
    const roles = [
      {
        _id: 'assistant-project-manager',
        name: 'Assistant Project Manager',
      },
      {
        _id: 'coordinator',
        name: 'Coordinator',
      },
      {
        _id: 'detailer',
        name: 'Detailer',
      },
      {
        _id: 'estimator',
        name: 'Estimator',
      },
      {
        _id: 'field-super',
        name: 'Field Super',
      },
      {
        _id: 'inventory-manager',
        name: 'Inventory Manager',
      },
      {
        _id: 'partner',
        name: 'Partner',
      },
      {
        _id: 'procurement-manager',
        name: 'Procurement Manager',
      },
      {
        _id: 'project-engineer',
        name: 'Project Engineer',
      },
      {
        _id: 'project-manager',
        name: 'Project Manager',
      },
      {
        _id: 'project-executive',
        name: 'Project Executive',
      },
      {
        _id: 'qa',
        name: 'QA',
      },
      {
        _id: 'safety-manager',
        name: 'Shipping Manager',
      },
      {
        _id: 'shop-super',
        name: 'Shop Super',
      },
    ];
    const permissionGroupList = store.state.availablePermissionGroups.map(group => ({ label: group.name, _id: group._id, value: group.name, name: group.name }));

    const data = reactive({
      currentUser: props.user,
      isLoading: false,
    });

    const validateFields = (user) => {
      let errMsg = null;
      if (_.isEmpty(user.firstName) || (!_.isEmpty(user.firstName) && user.firstName.length < 3)) {
        errMsg = 'First name must be at least three characters long';
      } else if (_.isEmpty(user.lastName) || (!_.isEmpty(user.lastName) && user.lastName.length < 1)) {
        errMsg = 'Last name must be at least one character long';
      } else if (_.isEmpty(user.email)) {
        errMsg = 'Email is mandatory';
      } else if (!_.isEmpty(user.email) && !Validation.validateEmail(user.email)) {
        errMsg = 'Please enter a valid email';
      }
      if (errMsg) return errMsg;
      if (!_.isEmpty(user.mobilePhone)
        && !Validation.validatePhoneNumber(user.mobilePhone)) {
        errMsg = 'Valid range for phone number length is 1 - 24!';
      }
      return errMsg;
    };

    onBeforeMount(() => {
      if (props.mode === 'create' && _.isEmpty(props.user)) {
        data.currentUser = {
          firstName: '',
          lastName: '',
          email: '',
          mobilePhone: '',
          role: roles[0],
          permissionGroup: permissionGroupList.find(l => l.name === 'View Only Member'),
          hasReservedLicense: false,
          autoAdd: false,
          addToCompanyProjects: false,
        };
      } else if (props.mode === 'edit' && !_.isEmpty(props.user)) {
        const currentUserGroup = props.user.permissionSettings?.find(perm => perm.company === store.state.userData.company)?.groups[0];
        data.currentUser = {
          ...props.user,
          role: roles.find(r => r._id === props.user.role),
          permissionGroup: permissionGroupList.find(l => l._id === currentUserGroup),
        };
      }
    });

    const disableUpdate = computed(() => {
      const user = data.currentUser;
      if (_.isEmpty(user.firstName) || (!_.isEmpty(user.firstName) && user.firstName.length < 3)) {
        return true;
      }else if(_.isEmpty(user.lastName) || (!_.isEmpty(user.lastName) && user.lastName.length < 1)) {
        return true;
      }else if (_.isEmpty(user.email) || (!_.isEmpty(user.email) && !Validation.validateEmail(user.email))) {
        return true;
      } else if (!_.isEmpty(user.mobilePhone)
        && !Validation.validatePhoneNumber(user.mobilePhone)) {
        return true;
      }
      return false;
    });


    const saveUser = async () => {
      const errMsg = validateFields(data.currentUser);
      if (errMsg) {
        toast.error(errMsg);
        return;
      }
      data.isLoading = true;
      try {
        let userData = {};
        if(props.mode === 'edit') {
          userData = props.user;
        } 

        userData = {
          ...userData,
          firstName: data.currentUser.firstName,
          lastName: data.currentUser.lastName,
          email: data.currentUser.email,
          mobilePhone: data.currentUser.mobilePhone,
          role: data.currentUser.role._id,
          permissionGroups: [data.currentUser.permissionGroup._id],
          hasReservedLicense: data.currentUser.hasReservedLicense,
          autoAdd: data.currentUser.autoAdd,
          addToCompanyProjects: data.currentUser.addToCompanyProjects,
          secondaryRoles: [],
        };
        if(props.mode === 'edit') {
          userData.permissionSettings.find(perm => perm.company === userData.company).groups = [data.currentUser.permissionGroup._id];
        }
        if (!userData.autoAdd) {
          userData.addToCompanyProjects = false;
          userData.secondaryRoles = [];
        }
        if (!props.user?.autoAdd && userData.autoAdd && !userData.secondaryRoles.includes("auto-add")) {
          await DialogProgrammatic.confirm({
            title: 'Add user to new projects?',
            message: `<p class="is-flex is-align-items-center"><i class="has-text-danger icon-alert is-size-1 mr-2"></i>
              The user will be added to all company Projects. Do you want to proceed?</p>`,
            okButton: 'Yes',
            cancelButton: 'No',
            onConfirm: async () => {
              try {
                userData.addToCompanyProjects = true;
                userData.secondaryRoles = ["auto-add"];
                await Users.editUser(userData);
                toast.success(`User ${userData.firstName} ${props.mode === 'create' ? 'created' : 'updated'} successfully`);
                emit('close', {refresh: true});
              } catch (error) {
                console.error('Error updating user', error);
                if (error.data?.message) {
                  toast.error(error.data.message);
                  return;
                }
                toast.error('Error updating user');
              }
            },
            onClose: async () => {
              userData.secondaryRoles = ["auto-add"];
              await Users.editUser(userData);
              toast.success(`User ${userData.firstName} ${props.mode === 'create' ? 'created' : 'updated'} successfully`);
              emit('close', {refresh: true});
            }
          });
        } else {
          if (props.user?.autoAdd) {
            userData.secondaryRoles = ["auto-add"];
          }
          await Users.editUser(userData);
          toast.success(`User ${userData.firstName} ${props.mode === 'create' ? 'created' : 'updated'} successfully`);
          emit('close', {refresh: true}); // Emit the created user data to the parent component
        }
      } catch (error) {
        console.error('Error creating user', error);
        if (error.data?.message) {
          toast.error(error.data.message);
          return;
        }
        toast.error('Error creating user');
      } finally {
        data.isLoading = false;
      }
    };

    const updateUser = () => {
      const errMsg = validateFields(data.currentUser);
      if (errMsg) {
        toast.error(errMsg);
        return;
      }
    };

    const disableField = (fieldType) => {
      if(props.mode !== 'edit') {
        return false;
      }
      if (fieldType === 'email' && ['Active'].includes(data.currentUser._statusForCompany)) {
        return true;
      }
      
      if (fieldType === 'license' && !store.state.userData.isOrgAdmin) {
        return true;
      }
      if (props.user.isOrgAdmin && props.user.wasAdmin && ['permissionGroup', 'license', 'addToProjects'].includes(fieldType)) {
        return true;
      }
      return false;
    };
    const updateLicense = () => {
      if (!data.currentUser.hasReservedLicense) {
        data.currentUser.permissionGroup = permissionGroupList.find(l => l.name === 'View Only Member');
      }
    };

    return {
      ...toRefs(data),
      permissionGroupList,
      roles,
      disableUpdate,
      disableField,
      updateLicense,
      saveUser
    };
  },
};
</script>
<style scoped>
.modal-card-head {
  background-color: #fff !important;
  border-bottom: none !important;
}

.modal-card-foot {
  border-top: 1px solid #84888c;
  background-color: #fff !important;
}

::v-deep(.o-field.has-text-centered.drag-drop) {
  text-align: start !important;
}

::v-deep(.o-upl) {
  height: auto !important;
  display: block !important;
}

.empty-box {
  height: 200px !important;
}
</style>
